import { Injectable, signal } from '@angular/core';
import { APP_VERSION } from '@env/version';

const LOCAL_STORAGE_DISMISSED_UPDATE = 'dismissedUpdate';
const LOCAL_STORAGE_UPDATES_SEEN = 'updatesSeen';
const CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes
const BRAND_NEW_THRESHOLD = 5 * 24 * 60 * 60 * 1000; // 5 days

@Injectable({ providedIn: 'root' })
export class VersionService {
  public updateAvailable = signal<boolean>(false);
  public versionIsBrandNew = signal<boolean>(false);
  public buildDateClient = signal<string | undefined>(undefined);
  public buildDateServer = signal<string | undefined>(undefined);

  constructor() {
    this.buildDateClient.set(APP_VERSION.buildDate);
    this.checkVersion();
    setInterval(() => this.checkVersion(), CHECK_INTERVAL);
    // Browser tab focus
    window.addEventListener('focus', () => this.checkVersion());
  }

  async checkVersion() {
    try {
      const response = await fetch(`/assets/version.json?t=${Date.now()}`, { cache: 'no-store' });
      const data = await response.json();
      const serverDate = data.buildDate;

      let updateAvailable = false;

      // check, if version is up to date
      if (serverDate && serverDate !== this.buildDateClient()) {
        const dismissed = localStorage.getItem(LOCAL_STORAGE_DISMISSED_UPDATE);
        if (dismissed !== serverDate) {
          console.log('[VersionService] New version available:', serverDate, ' (client:', this.buildDateClient(), ')');
          this.buildDateServer.set(serverDate);
          updateAvailable = true;
        }
      }

      this.updateAvailable.set(updateAvailable);

      // check if version is brand new
      const isBrandNew = this._isNewVersion(this.buildDateClient());
      const updateIsSeen = localStorage.getItem(LOCAL_STORAGE_UPDATES_SEEN) === this.buildDateClient();
      this.versionIsBrandNew.set(isBrandNew && !updateIsSeen);
    } catch (err) {
      console.error('[VersionService] Version check failed with', err);
    }
  }

  update() {
    window.location.reload();
  }

  dismiss() {
    const serverBuildDate = this.buildDateServer();
    if (serverBuildDate) {
      localStorage.setItem(LOCAL_STORAGE_DISMISSED_UPDATE, serverBuildDate);
      this.updateAvailable.set(false);
    }
  }

  setUpdateSeen() {
    const currentBuildDate = this.buildDateClient();
    if (currentBuildDate) {
      localStorage.setItem(LOCAL_STORAGE_UPDATES_SEEN, currentBuildDate);
      this.versionIsBrandNew.set(false);
    }
  }

  private _isNewVersion(dateString: string | undefined): boolean {
    if (!dateString) return false;

    const now = new Date();
    const buildDate = new Date(dateString);

    const timeDiff = now.getTime() - buildDate.getTime();
    return !isNaN(timeDiff) && timeDiff < BRAND_NEW_THRESHOLD;
  }
}
